const DEFAULT_FACE = 100;
const DEFAULT_BENCHMARK_COUPON = 0.05;
const DEFAULT_FREQUENCY = 2;

function assertFinitePositive(value, label) {
  if (!Number.isFinite(value) || value <= 0) {
    throw new Error(`${label} must be a positive finite number`);
  }
}

function normalizePoint(point) {
  const tenorYears = Number(point?.tenorYears ?? point?.tenor);
  let yieldRate = Number(point?.yieldRate ?? point?.yield);
  assertFinitePositive(tenorYears, "Curve tenor");
  if (!Number.isFinite(yieldRate)) throw new Error("Curve yield must be finite");
  if (Math.abs(yieldRate) > 1) yieldRate /= 100;
  return { tenorYears, yieldRate };
}

export function linearInterpolate(nodes, x) {
  if (!nodes.length) throw new Error("At least one interpolation node is required");
  if (x <= nodes[0][0]) return nodes[0][1];
  for (let index = 1; index < nodes.length; index += 1) {
    const [rightX, rightY] = nodes[index];
    if (x <= rightX) {
      const [leftX, leftY] = nodes[index - 1];
      const weight = (x - leftX) / (rightX - leftX);
      return leftY + weight * (rightY - leftY);
    }
  }
  const [lastX, lastY] = nodes[nodes.length - 1];
  const [priorX, priorY] = nodes[Math.max(0, nodes.length - 2)];
  if (lastX === priorX) return lastY;
  return lastY + ((lastY - priorY) / (lastX - priorX)) * (x - lastX);
}

export function bondPriceFromNominalYield({
  couponRate,
  yieldRate,
  periods,
  frequency = DEFAULT_FREQUENCY,
  face = DEFAULT_FACE,
}) {
  const coupon = (face * couponRate) / frequency;
  const periodicYield = yieldRate / frequency;
  let price = 0;
  for (let period = 1; period <= periods; period += 1) {
    price += coupon / (1 + periodicYield) ** period;
  }
  return price + face / (1 + periodicYield) ** periods;
}

/**
 * Builds a model-derived discount curve from a quoted municipal benchmark.
 * Each quote is interpreted as the nominal yield on a standardized coupon bond,
 * rather than as a zero rate. Intermediate semiannual yields are interpolated.
 */
export function bootstrapMunicipalBenchmarkCurve({
  points,
  benchmarkCouponRate = DEFAULT_BENCHMARK_COUPON,
  frequency = DEFAULT_FREQUENCY,
  maxYears,
}) {
  if (!Array.isArray(points) || points.length < 2) {
    throw new Error("At least two municipal benchmark points are required");
  }
  const normalized = points.map(normalizePoint).sort((a, b) => a.tenorYears - b.tenorYears);
  const yieldNodes = normalized.map((point) => [point.tenorYears, point.yieldRate]);
  const horizon = maxYears ?? normalized[normalized.length - 1].tenorYears;
  const periods = Math.ceil(horizon * frequency);
  const discountNodes = [[0, 1]];

  for (let period = 1; period <= periods; period += 1) {
    const tenorYears = period / frequency;
    const yieldRate = linearInterpolate(yieldNodes, tenorYears);
    const targetPrice = bondPriceFromNominalYield({
      couponRate: benchmarkCouponRate,
      yieldRate,
      periods: period,
      frequency,
    });
    const coupon = (DEFAULT_FACE * benchmarkCouponRate) / frequency;
    let priorCouponsPv = 0;
    for (let prior = 1; prior < period; prior += 1) {
      priorCouponsPv += coupon * discountNodes[prior][1];
    }
    const discountFactor = (targetPrice - priorCouponsPv) / (DEFAULT_FACE + coupon);
    if (!(discountFactor > 0) || discountFactor > 1.5) {
      throw new Error(`Invalid bootstrapped discount factor at ${tenorYears} years`);
    }
    discountNodes.push([tenorYears, discountFactor]);
  }

  const logDiscountNodes = discountNodes.map(([time, discountFactor]) => [time, Math.log(discountFactor)]);
  const discountFactor = (time) => {
    const t = Math.max(0, Number(time) || 0);
    return Math.exp(linearInterpolate(logDiscountNodes, t));
  };

  return {
    benchmarkCouponRate,
    frequency,
    points: normalized,
    discountNodes,
    discountFactor,
    zeroRate(time) {
      const t = Math.max(0, Number(time) || 0);
      return t === 0 ? normalized[0].yieldRate : -Math.log(discountFactor(t)) / t;
    },
  };
}

/**
 * Experimental alternative: interprets each quoted tenor yield as the par
 * coupon rate for that maturity. This is intentionally separate from
 * bootstrapMunicipalBenchmarkCurve, which treats the quote as the nominal
 * yield of a fixed-coupon standardized 5% bond.
 */
export function bootstrapMunicipalParYieldCurve({
  points,
  frequency = DEFAULT_FREQUENCY,
  maxYears,
}) {
  if (!Array.isArray(points) || points.length < 2) {
    throw new Error("At least two municipal benchmark points are required");
  }
  const normalized = points.map(normalizePoint).sort((a, b) => a.tenorYears - b.tenorYears);
  const yieldNodes = normalized.map((point) => [point.tenorYears, point.yieldRate]);
  const horizon = maxYears ?? normalized[normalized.length - 1].tenorYears;
  const periods = Math.ceil(horizon * frequency);
  const discountNodes = [[0, 1]];

  for (let period = 1; period <= periods; period += 1) {
    const tenorYears = period / frequency;
    const parYield = linearInterpolate(yieldNodes, tenorYears);
    const coupon = (DEFAULT_FACE * parYield) / frequency;
    let priorCouponsPv = 0;
    for (let prior = 1; prior < period; prior += 1) {
      priorCouponsPv += coupon * discountNodes[prior][1];
    }
    const discountFactor = (DEFAULT_FACE - priorCouponsPv) / (DEFAULT_FACE + coupon);
    if (!(discountFactor > 0) || discountFactor > 1.5) {
      throw new Error(`Invalid par-yield discount factor at ${tenorYears} years`);
    }
    discountNodes.push([tenorYears, discountFactor]);
  }

  const logDiscountNodes = discountNodes.map(([time, discountFactor]) => [time, Math.log(discountFactor)]);
  const discountFactor = (time) => {
    const t = Math.max(0, Number(time) || 0);
    return Math.exp(linearInterpolate(logDiscountNodes, t));
  };

  return {
    interpretation: "par-yield",
    frequency,
    points: normalized,
    discountNodes,
    discountFactor,
    zeroRate(time) {
      const t = Math.max(0, Number(time) || 0);
      return t === 0 ? normalized[0].yieldRate : -Math.log(discountFactor(t)) / t;
    },
  };
}

